import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes, FaTicketAlt, FaPlus, FaEdit, FaGift, FaPercent, FaDollarSign, FaCalendar, FaTag, FaFileAlt } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import { catchError, commonModalSetting } from "JS/common";
import { createCouponMember, createPromotionCoupon, createRewardCoupon, updateCoupon } from "JS/API";

// 디자인 시스템 임포트
import { designTokens, commonStyles } from "utils/designSystem";
import { detailModalStyles } from "utils/modalDesignSystem";

const CreateCouponModal = ({
  setIsOpenCouponModal,
  getList,
  editCouponData,
  onSuccessCreate,
  mode,
  userPks = [],
}) => {

  const isMemberMode = mode === "member";

  const [formData, setFormData] = useState({
    coupon_type: "promotion",
    name: "",
    code: "",
    desc: "",
    discount_type: "percent",
    discount_value: "",
    start_date: "",
    end_date: "",
  });

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });

  const navigate = useNavigate();

  // 스타일 정의
  const modalStyles = {
    overlay: detailModalStyles.overlay,
    container: {
      backgroundColor: designTokens.colors.surface,
      borderRadius: designTokens.borderRadius.xl,
      boxShadow: designTokens.shadows.lg,
      width: '100%',
      maxWidth: '700px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: detailModalStyles.header,
    title: detailModalStyles.title,
    closeButton: detailModalStyles.closeButton,
    content: detailModalStyles.content,
    formSection: {
      marginBottom: designTokens.spacing.xl
    },
    formLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      fontSize: '14px',
      fontWeight: '600',
      color: designTokens.colors.text.primary,
      marginBottom: designTokens.spacing.md
    },
    formInput: {
      ...commonStyles.input,
      marginBottom: designTokens.spacing.lg
    },
    radioGroup: {
      display: 'flex',
      gap: designTokens.spacing.xl,
      marginBottom: designTokens.spacing.lg
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      fontSize: '14px',
      color: designTokens.colors.text.secondary,
      cursor: 'pointer'
    },
    radioInput: {
      width: '16px',
      height: '16px',
      cursor: 'pointer'
    },
    dateGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md
    },
    dateInput: {
      ...commonStyles.input,
      marginBottom: 0,
      flex: 1
    },
    dateSeparator: {
      fontSize: '14px',
      color: designTokens.colors.text.secondary,
      fontWeight: '500'
    },
    numberInput: {
      ...commonStyles.input,
      marginBottom: 0
    },
    helpText: {
      fontSize: '12px',
      color: designTokens.colors.text.tertiary,
      marginTop: designTokens.spacing.xs,
      fontStyle: 'italic'
    },
    actionButtons: detailModalStyles.actionButtons,
    primaryButton: detailModalStyles.primaryButton,
    secondaryButton: detailModalStyles.secondaryButton};

  useEffect(() => {
    if (isMemberMode) {
      setFormData((prev) => ({ ...prev, coupon_type: "reward" }));
    }
  }, [isMemberMode]);

  useEffect(() => {
    if (editCouponData) {
      setFormData({
        coupon_type: editCouponData.coupon_type === "프로모션" ? "promotion" : "reward",
        name: editCouponData.name,
        code: editCouponData.coupon_code || "",
        desc: editCouponData?.description || "",
        discount_type: editCouponData.discount_type === "discount" ? "percent" : "price",
        discount_value: editCouponData.discount_val,
        start_date: editCouponData.start_date?.split("T")[0] || "",
        end_date: editCouponData.end_date?.split("T")[0] || "",
      });
    }
  }, [editCouponData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    const {
      coupon_type,
      name,
      code,
      desc,
      discount_type,
      discount_value,
      start_date,
      end_date,
    } = formData;

    if (
      !name ||
      !discount_value ||
      !start_date ||
      !end_date ||
      (coupon_type !== "reward" && !code)
    ) {
      return commonModalSetting(setAlertBox, true, "alert", "필수 항목을 모두 입력해주세요.");
    }
    const today = new Date().setHours(0, 0, 0, 0);
    const start = new Date(start_date).setHours(0, 0, 0, 0);
    const end = new Date(end_date).setHours(0, 0, 0, 0);

    if (end < today) {
      return commonModalSetting(setAlertBox, true, "alert", "유효기간이 지난 쿠폰은 생성할 수 없습니다.");
    }

    if (start > end) {
      return commonModalSetting(setAlertBox, true, "alert", "시작일은 종료일보다 이전이어야 합니다.");
    }
    const mappedDiscountType = discount_type === "percent" ? "discount" : "amount";

    const payload = {
      name,
      description: desc,
      discount_type: mappedDiscountType,
      discount_value: Number(discount_value),
      start_date: new Date(start_date).toISOString(),
      end_date: new Date(end_date).toISOString(),
    };

    if (isMemberMode) {
      payload.user_pk_list = userPks;
    } else if (coupon_type !== "reward") {
      payload.coupon_code = code;
    }

    try {
      let result;
      if (editCouponData) {
        result = await updateCoupon(editCouponData.id, code, payload);
      } else if (isMemberMode) {
        result = await createCouponMember(payload);
      } else {
        result =
          coupon_type === "reward"
            ? await createRewardCoupon(payload)
            : await createPromotionCoupon(payload);
      }

      if (typeof result === "object") {
        const message = editCouponData ? "쿠폰이 수정되었습니다." : "쿠폰이 생성되었습니다.";
        commonModalSetting(setAlertBox, true, "alert", message);
        getList?.();
        setIsOpenCouponModal(false);
      }
    } catch (error) {
      await catchError(error, navigate, setAlertBox, setAlert);
    }
  };

  return (
    <>
      <div style={modalStyles.overlay}>
        <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
          {/* 헤더 */}
          <div style={modalStyles.header}>
            <h3 style={modalStyles.title}>
              {editCouponData ? <FaEdit /> : <FaPlus />}
              {editCouponData ? "쿠폰 수정" : "쿠폰 생성"}
            </h3>
            <button
              style={modalStyles.closeButton}
              onClick={() => setIsOpenCouponModal(false)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = designTokens.colors.background;
                e.target.style.color = designTokens.colors.text.primary;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = designTokens.colors.text.tertiary;
              }}
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* 컨텐츠 */}
          <div style={modalStyles.content}>
            {/* 쿠폰 유형 */}
            {!editCouponData && !isMemberMode && (
              <div style={modalStyles.formSection}>
                <label style={modalStyles.formLabel}>
                  <FaTag />
                  쿠폰 유형
                </label>
                <div style={modalStyles.radioGroup}>
                  <label style={modalStyles.radioLabel}>
                    <input
                      type="radio"
                      name="coupon_type"
                      value="promotion"
                      checked={formData.coupon_type === "promotion"}
                      onChange={(e) => handleChange("coupon_type", e.target.value)}
                      style={modalStyles.radioInput}
                    />
                    <FaGift size={14} />
                    프로모션
                  </label>
                  <label style={modalStyles.radioLabel}>
                    <input
                      type="radio"
                      name="coupon_type"
                      value="reward"
                      checked={formData.coupon_type === "reward"}
                      onChange={(e) => handleChange("coupon_type", e.target.value)}
                      style={modalStyles.radioInput}
                    />
                    <FaTicketAlt size={14} />
                    지급쿠폰
                  </label>
                </div>
              </div>
            )}

            {/* 쿠폰명 */}
            <div style={modalStyles.formSection}>
              <label style={modalStyles.formLabel}>
                <FaTicketAlt />
                쿠폰명 *
              </label>
              <input
                type="text"
                placeholder="ex) 마크픽 1주년 쿠폰"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                style={modalStyles.formInput}
              />
            </div>

            {/* 쿠폰 코드 */}
            {formData.coupon_type !== "reward" && (
              <div style={modalStyles.formSection}>
                <label style={modalStyles.formLabel}>
                  <FaTag />
                  쿠폰 코드 *
                </label>
                <input
                  type="text"
                  placeholder="사용자가 입력할 쿠폰 코드를 입력하세요. ex) 마크픽 1주년 축하합니다"
                  value={formData.code}
                  onChange={(e) => handleChange("code", e.target.value)}
                  style={modalStyles.formInput}
                />
              </div>
            )}

            {/* 쿠폰 상세 */}
            <div style={modalStyles.formSection}>
              <label style={modalStyles.formLabel}>
                <FaFileAlt />
                쿠폰 상세 (선택)
              </label>
              <input
                type="text"
                placeholder="ex) 김마크 회원 단체 출원이니까 할인해달라고 전화옴"
                value={formData.desc}
                onChange={(e) => handleChange("desc", e.target.value)}
                style={modalStyles.formInput}
              />
              <div style={modalStyles.helpText}>
                사용자 화면에서는 보이지 않습니다.
              </div>
            </div>

            {/* 쿠폰 금액 */}
            <div style={modalStyles.formSection}>
              <label style={modalStyles.formLabel}>
                <FaDollarSign />
                쿠폰 금액 *
              </label>
              <div style={modalStyles.radioGroup}>
                <label style={modalStyles.radioLabel}>
                  <input
                    type="radio"
                    name="discount_type"
                    value="percent"
                    checked={formData.discount_type === "percent"}
                    onChange={(e) => handleChange("discount_type", e.target.value)}
                    style={modalStyles.radioInput}
                  />
                  <FaPercent size={14} />
                  퍼센트(%)
                </label>
                <label style={modalStyles.radioLabel}>
                  <input
                    type="radio"
                    name="discount_type"
                    value="price"
                    checked={formData.discount_type === "price"}
                    onChange={(e) => handleChange("discount_type", e.target.value)}
                    style={modalStyles.radioInput}
                  />
                  <FaDollarSign size={14} />
                  가격 할인
                </label>
              </div>

              {formData.discount_type === "percent" ? (
                <>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    placeholder="1~100 사이 숫자 입력"
                    value={formData.discount_value}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "") {
                        handleChange("discount_value", "");
                        return;
                      }
                      const num = Number(val);
                      if (num < 1 || num > 100) return;
                      handleChange("discount_value", num);
                    }}
                    style={modalStyles.numberInput}
                  />
                  <div style={modalStyles.helpText}>
                    1~100 사이의 숫자를 입력하세요.
                  </div>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="숫자만 입력하세요"
                    value={
                      formData.discount_value !== ""
                        ? Number(formData.discount_value).toLocaleString()
                        : ""
                    }
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, "");
                      if (/^\d*$/.test(rawValue)) {
                        handleChange("discount_value", rawValue);
                      }
                    }}
                    style={modalStyles.numberInput}
                  />
                  <div style={modalStyles.helpText}>
                    할인할 금액을 입력하세요. (숫자만)
                  </div>
                </>
              )}
            </div>

            {/* 쿠폰 기간 */}
            <div style={modalStyles.formSection}>
              <label style={modalStyles.formLabel}>
                <FaCalendar />
                쿠폰 기간 *
              </label>
              <div style={modalStyles.dateGroup}>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange("start_date", e.target.value)}
                  style={modalStyles.dateInput}
                />
                <span style={modalStyles.dateSeparator}>~</span>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleChange("end_date", e.target.value)}
                  style={modalStyles.dateInput}
                />
              </div>
              <div style={modalStyles.helpText}>
                쿠폰 사용 가능 기간을 설정하세요.
              </div>
            </div>
          </div>

          {/* 액션 버튼 */}
          <div style={modalStyles.actionButtons}>
            <button
              style={modalStyles.primaryButton}
              onClick={handleSubmit}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = designTokens.colors.primaryHover || '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = designTokens.colors.primary;
              }}
            >
              {editCouponData ? (
                <>
                  <FaEdit size={12} />
                  수정
                </>
              ) : (
                <>
                  <FaPlus size={12} />
                  생성
                </>
              )}
            </button>
            <button
              style={modalStyles.secondaryButton}
              onClick={() => setIsOpenCouponModal(false)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = designTokens.colors.background;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = designTokens.colors.surface;
              }}
            >
              취소
            </button>
          </div>
        </div>
      </div>

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
          }}
        />
      )}
    </>
  );
};

export default CreateCouponModal;